import React, { forwardRef, PropsWithChildren } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import {
  composeStagePath,
  composeTournamentPath,
  composeUnionPath,
} from 'app/modules/Navigation/utils';
import { BreadcrumbType } from 'app/redux/betting-presentation/grid/constants';
import type { GridSubheaderBreadcrumb } from 'app/redux/betting-presentation/grid/selectors';

import styles from './Breadcrumb.module.scss';

type BreadcrumbLinkProps = PropsWithChildren<{
  breadcrumb: GridSubheaderBreadcrumb;
  className?: string;
  title?: string;
  nonClickableSportsEvent?: boolean;
  dataGaEvent: string;
}>;

const BreadcrumbLink = forwardRef<HTMLAnchorElement, BreadcrumbLinkProps>(({
  children,
  breadcrumb,
  className,
  title,
  nonClickableSportsEvent,
  dataGaEvent,
}, ref) => {
  const { name, pathname } = breadcrumb;
  const dataGaAttributes = getDataGaEventValueByBreadcrumb(dataGaEvent, breadcrumb);
  const _className = classNames(styles.link, className);
  const link = getBreadcrumbLink(breadcrumb);

  if (!link) {
    return null;
  }

  if (pathname === null || nonClickableSportsEvent) {
    return (
      <span className={_className}
            title={title}
            ref={ref}>
        {name}
        {children}
      </span>
    );
  }

  return (
    <Link className={_className}
          {...dataGaAttributes}
          title={title}
          to={link}
          innerRef={ref}>
      {name}
      {children}
    </Link>
  );
});

function getBreadcrumbLink(breadcrumb: GridSubheaderBreadcrumb) {
  const {
    pathname,
    sportId,
    tournamentUnionId,
    tournamentId,
    tournamentStageId,
  } = breadcrumb;

  switch (breadcrumb.type) {
    case BreadcrumbType.SPORT:
      return composeUnionPath({ sportId }, pathname);

    case BreadcrumbType.UNION:
      return composeUnionPath({ sportId, tournamentUnionId }, pathname);

    case BreadcrumbType.TOURNAMENT:
      return composeTournamentPath({ sportId, tournamentUnionId, tournamentId }, pathname);

    case BreadcrumbType.STAGE:
      return composeStagePath({
        sportId,
        tournamentUnionId,
        tournamentId,
        tournamentStageId,
      }, pathname);

    default:
      assertExhaustiveness(breadcrumb.type);
      console.error('Wrong breadcrumb type = ', breadcrumb.type);
      return null;
  }
}

function getDataGaEventValueByBreadcrumb(
  gaEvent: string,
  breadcrumb: GridSubheaderBreadcrumb,
) {
  const {
    sportId,
    tournamentUnionId,
    tournamentId,
    tournamentStageId,
  } = breadcrumb;

  switch (breadcrumb.type) {
    case BreadcrumbType.SPORT:
      return {
        ['data-ga-event']: gaEvent,
        ['data-ga-event-value']: 'sport',
        ['data-ga-sport-category']: sportId,
      };
    case BreadcrumbType.UNION:
      return {
        ['data-ga-event']: gaEvent,
        ['data-ga-event-value']: 'union',
        ['data-ga-sport-country']: tournamentUnionId,
      };
    case BreadcrumbType.TOURNAMENT:
      return {
        ['data-ga-event']: gaEvent,
        ['data-ga-event-value']: 'tournament',
        ['data-ga-sport-league']: tournamentId,
      };
    case BreadcrumbType.STAGE:
      return {
        ['data-ga-event']: gaEvent,
        ['data-ga-event-value']: 'stage',
        ['data-ga-sport-championship']: tournamentStageId,
      };

    default:
      assertExhaustiveness(breadcrumb.type);
      return {};
  }
}

export default BreadcrumbLink;
